/**
 * Meta Pixel (Facebook Pixel) tracking helpers.
 * Loaded post-consent via MetaPixelLoader (W3.1) — not in static index.html.
 */

export const META_PIXEL_ID = '1487313132571487';

const IS_PROD =
  typeof import.meta !== 'undefined' &&
  (import.meta.env?.VITE_APP_ENV === 'production' || import.meta.env?.PROD === true);

let pixelInitStarted = false;

/** Lazy-load fbevents.js after marketing cookie consent (W3.1). */
export function initMetaPixel(): void {
  if (!IS_PROD || pixelInitStarted || typeof window === 'undefined') return;
  pixelInitStarted = true;

  if (typeof window.fbq === 'function') {
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
    return;
  }

  interface FbqStub {
    (...args: unknown[]): void;
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[][];
    loaded: boolean;
    version: string;
    push: FbqStub;
  }

  const n = function (this: unknown, ...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args);
    } else {
      n.queue.push(args);
    }
  } as FbqStub;

  n.queue = [];
  n.loaded = true;
  n.version = '2.0';
  n.push = n;
  window.fbq = n;
  if (!window._fbq) window._fbq = n;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const first = document.getElementsByTagName('script')[0];
  first?.parentNode?.insertBefore(script, first);

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}

const hasFbq = (): boolean =>
  IS_PROD &&
  typeof window !== 'undefined' &&
  typeof window.fbq === 'function';

/**
 * Track CTA click (Lead) – use for buttons that lead to signup/login/optimize.
 * Meta requires `value` and `currency` for ROAS calculations.
 * Default lead value = ₹50 (estimated value of a qualified lead based on
 * ₹349/month plan and ~15% conversion assumption).
 */
export function trackCTA(contentName?: string, value: number = 50, currency: string = 'INR'): void {
  if (!hasFbq()) return;
  try {
    window.fbq('track', 'Lead', {
      value,
      currency,
      ...(contentName ? { content_name: contentName } : {}),
    });
  } catch (e) {
    console.warn('Meta Pixel Lead failed:', e);
  }
}

/**
 * Track signup completion (CompleteRegistration).
 * Value = ₹100 (higher than lead — user committed to creating an account).
 */
export function trackSignup(value: number = 100, currency: string = 'INR'): void {
  if (!hasFbq()) return;
  try {
    window.fbq('track', 'CompleteRegistration', { value, currency });
  } catch (e) {
    console.warn('Meta Pixel CompleteRegistration failed:', e);
  }
}

/**
 * Track login (custom event for consistency with OAuth flow).
 */
export function trackLogin(): void {
  if (!hasFbq()) return;
  try {
    window.fbq('trackCustom', 'Login');
  } catch (e) {
    console.warn('Meta Pixel Login failed:', e);
  }
}

/**
 * Track logout (custom event).
 */
export function trackLogout(): void {
  if (!hasFbq()) return;
  try {
    window.fbq('trackCustom', 'Logout');
  } catch (e) {
    console.warn('Meta Pixel Logout failed:', e);
  }
}

/**
 * Track start of checkout (e.g. when user proceeds to payment for credits).
 */
export function trackInitiateCheckout(value?: number, currency: string = 'INR'): void {
  if (!hasFbq()) return;
  try {
    window.fbq('track', 'InitiateCheckout', value != null ? { value, currency } : undefined);
  } catch (e) {
    console.warn('Meta Pixel InitiateCheckout failed:', e);
  }
}

/**
 * Track purchase (credits or subscription). Use after payment is confirmed.
 */
export function trackPurchase(value: number, currency: string = 'INR', orderId?: string): void {
  if (!hasFbq()) return;
  try {
    const params: Record<string, string | number> = { value, currency };
    if (orderId) params.content_ids = orderId;
    window.fbq('track', 'Purchase', params);
  } catch (e) {
    console.warn('Meta Pixel Purchase failed:', e);
  }
}
